import * as React from 'react';
import { Grip } from 'lucide-react';
import { cn } from '@higgins/utils';
import { HigginsLogo } from './higgins-logo';
import { FeedbackButton, FeedbackLinkButton } from './feedback-button';
import type { FeedbackFormProps } from './feedback-form';
import { HelpPanel } from './help-panel';

/** The Higgins Apps portal — where the launcher and the logo go. */
const PORTAL_URL = 'https://apps.higgins.com.au';

/** What the app's own home link is given to render: its look, its label and the title block. */
interface HomeLinkProps {
  className: string;
  'aria-label': string;
  children: React.ReactNode;
}

/** No router: an ordinary link to the app's root, which reloads the page. */
const plainHomeLink = (props: HomeLinkProps) => <a href="/" {...props} />;

/**
 * The Report a problem / Suggest button (#511): the app's own API saves the report, or, for an
 * app with no API (`viaAppFeedback`), App Feedback's report page opens in a new tab.
 */
export type AppHeaderFeedback = FeedbackFormProps | { appKey: string; viaAppFeedback: true };

export interface AppHeaderProps {
  /** App name, shown beside the logo. */
  title: string;
  /** One-line description under the title. */
  subtitle?: string;
  /** Right-hand toolbar: signed-in user, sign-out. The feedback button and Help follow it. */
  children?: React.ReactNode;
  /** Report a problem / Suggest, just left of Help. Every web app shows it but the portal and, until C3, GM Report. */
  feedback?: AppHeaderFeedback;
  /** The app's docs/USERS.md (`?raw` import) and the panel's title: Help, rightmost. Icon only unless `iconOnly` is false. */
  help?: { source: string; title: string; iconOnly?: boolean };
  /** Width of the inner container. Defaults to `max-w-5xl`. Match the page's `<main>`. */
  containerClassName?: string;
  /** Extra classes for the outer header bar (e.g. `pt-safe-top` for the iPhone notch). */
  className?: string;
  /** The waffle back to the portal. Defaults to on; only the portal itself turns it off. */
  launcher?: boolean;
  /** A row under the bar, inside the sticky header — e.g. an app's section tabs. */
  subNav?: React.ReactNode;
  /**
   * Renders the title's link to the app's home page (#435). Defaults to a plain link to `/`,
   * which reloads the page; an app on react-router passes `(p) => <Link to="/" {...p} />`
   * so going home stays inside the app (and works offline).
   */
  homeLink?: (props: HomeLinkProps) => React.ReactNode;
}

/**
 * The standard Higgins app header: launcher, logo, app title (a link to the app's home), right-hand toolbar,
 * sticky and translucent.
 * Compact on phones, roomier from the `sm` breakpoint up — one header for every screen size.
 * Use this rather than hand-writing a header, so every app's header changes together.
 */
export function AppHeader({
  title,
  subtitle,
  children,
  feedback,
  help,
  containerClassName,
  className,
  launcher = true,
  subNav,
  homeLink = plainHomeLink,
}: AppHeaderProps) {
  return (
    <header className={cn('bg-white/70 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10', className)}>
      <div className={cn('max-w-5xl mx-auto px-4 sm:px-6', containerClassName)}>
        <div className="flex justify-between items-center gap-2 py-3 sm:py-4">
          <div className="flex min-w-0 items-center gap-1.5 sm:gap-3">
            {launcher && (
              // M365-style waffle, far left — straight back to the portal.
              <a
                href={PORTAL_URL}
                aria-label="Higgins Apps"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                <Grip className="h-5 w-5" />
              </a>
            )}
            <a href={PORTAL_URL} aria-label="Higgins Apps portal" className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500">
              <HigginsLogo className="h-8 sm:h-10 w-auto select-none" />
            </a>
            <div className="min-w-0 pl-2 sm:pl-3 border-l border-slate-200">
              {homeLink({
                'aria-label': `${title} home`,
                // At least 44 px tall for a finger on the iPad; the negative margin keeps the bar's height.
                className:
                  'group -my-0.5 flex min-h-11 min-w-0 flex-col justify-center rounded-md py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
                children: (
                  <>
                    <h1 className="truncate font-bold text-base sm:text-lg tracking-tight text-slate-900 group-hover:text-slate-600">{title}</h1>
                    {subtitle && <p className="truncate text-xs text-slate-500 group-hover:text-slate-400">{subtitle}</p>}
                  </>
                ),
              })}
            </div>
          </div>
          {(children || feedback || help) && (
            <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
              {children}
              {feedback && ('viaAppFeedback' in feedback ? <FeedbackLinkButton appKey={feedback.appKey} iconOnly /> : <FeedbackButton {...feedback} iconOnly />)}
              {/* Rightmost toolbar item, per the repo's in-app help convention. */}
              {help && <HelpPanel source={help.source} title={help.title} iconOnly={help.iconOnly ?? true} />}
            </div>
          )}
        </div>
        {subNav}
      </div>
    </header>
  );
}
